import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  BellIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  Bars3Icon,
  CakeIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { employeeService } from '../../services/employeeService';
import { authService } from '../../services/authService';
import { getProfilePhotoUrl } from '../../utils/profilePhoto';

interface HeaderProps {
  title?: string;
  onMenuClick: () => void;
}

export function Header({ title, onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [birthdaysToday, setBirthdaysToday] = useState<{ id: number; firstName: string; lastName: string }[]>([]);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [pendingRegistrationCount, setPendingRegistrationCount] = useState(0);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchBirthdays = async () => {
      try {
        const list = await employeeService.getBirthdaysToday();
        setBirthdaysToday(list);
      } catch {
        setBirthdaysToday([]);
      }
    };
    fetchBirthdays();
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await authService.getNotifications();
      setPendingLeaveCount(data.pendingLeaveCount);
      setPendingRegistrationCount(data.pendingRegistrationCount);
    } catch {
      setPendingLeaveCount(0);
      setPendingRegistrationCount(0);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const loadProfilePhoto = async () => {
      if (!user?.employeeId) return;
      try {
        const profile = await employeeService.getMyProfile();
        setProfilePhotoUrl(getProfilePhotoUrl(profile.photoPath) ?? null);
      } catch {
        setProfilePhotoUrl(null);
      }
    };
    loadProfilePhoto();
  }, [user?.employeeId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-gray-200 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-6">
        {/* Mobile menu button and page title */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center -ml-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          {title && (
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{title}</h1>
          )}
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Notifications: leave approvals, pending registrations, birthdays */}
          <Menu as="div" className="relative">
            <Menu.Button
              className="relative p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
              onClick={() => fetchNotifications()}
            >
              <BellIcon className="w-5 h-5" />
              {(birthdaysToday.length > 0 || pendingLeaveCount > 0 || pendingRegistrationCount > 0) && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary-500 rounded-full" />
              )}
            </Menu.Button>
            <Menu.Items className="absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 focus:outline-none z-50 max-h-[70vh] overflow-y-auto">
              {/* Action required: leave & registrations */}
              {(pendingLeaveCount > 0 || pendingRegistrationCount > 0) && (
                <>
                  <div className="px-4 py-2 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Action required</h3>
                  </div>
                  {pendingLeaveCount > 0 && (
                    <Link
                      to="/leave"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => (document.activeElement as HTMLElement)?.blur?.()}
                    >
                      <span className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                        <ClipboardDocumentListIcon className="w-5 h-5" />
                      </span>
                      <span>
                        <strong>{pendingLeaveCount}</strong> leave request{pendingLeaveCount !== 1 ? 's' : ''} need approval
                      </span>
                    </Link>
                  )}
                  {pendingRegistrationCount > 0 && (
                    <Link
                      to="/pending-registrations"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => (document.activeElement as HTMLElement)?.blur?.()}
                    >
                      <span className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                        <UserPlusIcon className="w-5 h-5" />
                      </span>
                      <span>
                        <strong>{pendingRegistrationCount}</strong> registration{pendingRegistrationCount !== 1 ? 's' : ''} pending
                      </span>
                    </Link>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                </>
              )}
              <div className="px-4 py-2 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <CakeIcon className="w-5 h-5 text-primary-500" />
                  Today&apos;s Birthdays
                </h3>
              </div>
              {birthdaysToday.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-500">No birthdays today</p>
              ) : (
                <div className="py-1">
                  {birthdaysToday.map((emp) => (
                    <div
                      key={emp.id}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-medium">
                        {emp.firstName[0]}{emp.lastName[0]}
                      </span>
                      <span>
                        <strong>{emp.firstName} {emp.lastName}</strong> celebrates birthday today! 🎂
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Menu.Items>
          </Menu>

          {/* User menu - profile picture or initials */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2 p-2 min-h-[44px] hover:bg-gray-100 rounded-lg transition-colors touch-manipulation">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-primary-100">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-semibold text-primary-600">
                    {user?.employee?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-gray-700">
                {user?.employee?.firstName || user?.email?.split('@')[0]}
              </span>
            </Menu.Button>

            <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 focus:outline-none z-50 min-w-[12rem]">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/profile')}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700`}
                  >
                    <UserCircleIcon className="w-5 h-5" />
                    My Profile
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate('/settings')}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700`}
                  >
                    <Cog6ToothIcon className="w-5 h-5" />
                    Settings
                  </button>
                )}
              </Menu.Item>
              <div className="border-t border-gray-200 my-1" />
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogout}
                    className={`${
                      active ? 'bg-gray-50' : ''
                    } flex items-center gap-3 w-full px-4 py-2 text-sm text-danger-600`}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    Logout
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      </div>
    </header>
  );
}
